import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import type { ContentStatus, LeadStatus, NewsletterStatus } from '@/lib/api/types';

/**
 * Lifecycle status as a pill (DESIGN pill radius + mono label). Color is NEVER the only
 * signal — every pill carries its label text + a leading glyph (PRODUCT a11y).
 */
type Tone = 'neutral' | 'accent' | 'amber' | 'teal' | 'mute' | 'error';

const toneClass: Record<Tone, string> = {
  neutral: 'bg-canvas-deep-sand text-ink-mute',
  accent: 'bg-accent-blue text-canvas-paper',
  amber: 'bg-signal-amber-fill text-signal-amber-ink',
  teal: 'bg-signal-teal text-canvas-paper',
  mute: 'bg-canvas-deep-sand text-ink-mid',
  error: 'bg-signal-error-fill text-signal-error',
};

const CONTENT: Record<ContentStatus, { tone: Tone; glyph: string }> = {
  draft: { tone: 'neutral', glyph: '○' },
  in_review: { tone: 'accent', glyph: '◐' },
  scheduled: { tone: 'amber', glyph: '◔' },
  published: { tone: 'teal', glyph: '●' },
  archived: { tone: 'mute', glyph: '⊘' },
};

const LEAD: Record<LeadStatus, { tone: Tone; glyph: string }> = {
  new: { tone: 'accent', glyph: '●' },
  in_progress: { tone: 'amber', glyph: '◐' },
  reviewing: { tone: 'amber', glyph: '◔' },
  shortlisted: { tone: 'teal', glyph: '★' },
  responded: { tone: 'accent', glyph: '↩' },
  hired: { tone: 'teal', glyph: '✓' },
  rejected: { tone: 'mute', glyph: '✕' },
  closed: { tone: 'mute', glyph: '⊘' },
  spam: { tone: 'error', glyph: '⚑' },
  archived: { tone: 'mute', glyph: '⊘' },
};

const NEWSLETTER: Record<NewsletterStatus, { tone: Tone; glyph: string }> = {
  confirmed: { tone: 'teal', glyph: '✓' },
  pending: { tone: 'amber', glyph: '◔' },
  unsubscribed: { tone: 'mute', glyph: '⊘' },
};

const pillBase =
  'inline-flex items-center gap-[5px] rounded-pill px-[10px] py-[3px] font-mono text-label uppercase tracking-[0.08em]';

/**
 * A lead pill's look, for a control that must read as the pill it replaces —
 * the lead list's status dropdown (B-R16-04) — rather than as a form field.
 */
export function leadStatusLook(status: LeadStatus): { className: string; glyph: string } {
  const { tone, glyph } = LEAD[status] ?? LEAD.new;
  return { className: cn(pillBase, toneClass[tone]), glyph };
}

function Pill({ tone, glyph, label }: { tone: Tone; glyph: string; label: string }) {
  return (
    <span className={cn(pillBase, toneClass[tone])}>
      <span aria-hidden>{glyph}</span>
      {label}
    </span>
  );
}

export function ContentStatusPill({ status }: { status: ContentStatus }) {
  const { t } = useTranslation();
  return <Pill {...CONTENT[status]} label={t(`status.content.${status}`)} />;
}
export function LeadStatusPill({ status }: { status: LeadStatus }) {
  const { t } = useTranslation();
  return <Pill {...LEAD[status]} label={t(`status.lead.${status}`)} />;
}
export function NewsletterStatusPill({ status }: { status: NewsletterStatus }) {
  const { t } = useTranslation();
  return <Pill {...NEWSLETTER[status]} label={t(`status.newsletter.${status}`)} />;
}
